import React, { useState } from 'react';
import { Form, Formik } from 'formik';

import AlertBanner from '~/components/core/AlertBanner';
import StepperComponent from '~/components/core/Stepper';
import type { StepProps } from '~/components/core/Stepper/Step';
import { reportAxiosError } from '~/Utils';

import { useAiClaimImportActions } from './hooks/useAiClaimImportActions';
import ClaimNumbersInputStep, { parseClaimNumbers } from './steps/ClaimNumbersInputStep';
import ClaimPreviewStep from './steps/ClaimPreviewStep';
import ImportConfirmationStep from './steps/ImportConfirmationStep';
import type { AiClaimImportPreviewByClaimsResponse, AiClaimImportStartResponse } from './types';

const Stepper = StepperComponent as typeof StepperComponent & {
  Step: React.ComponentType<StepProps>;
};

interface ImportByClaimsDialogProps {
  open: boolean;
  onClose: () => void;
  onImportStarted?: () => void;
}

const ImportByClaimsDialog: React.FC<ImportByClaimsDialogProps> = ({ open, onClose, onImportStarted }) => {
  const { fetchPreviewByClaims, startImportByClaims } = useAiClaimImportActions();

  const [claimNumbers, setClaimNumbers] = useState('');
  const [previewData, setPreviewData] = useState<AiClaimImportPreviewByClaimsResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [startResponse, setStartResponse] = useState<AiClaimImportStartResponse | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const handleClose = () => {
    setClaimNumbers('');
    setPreviewData(null);
    setIsPreviewLoading(false);
    setStartResponse(null);
    setIsStarting(false);
    onClose();
  };

  const handlePreview = async (): Promise<boolean> => {
    const parsed = parseClaimNumbers(claimNumbers);
    if (parsed.length === 0) return false;

    setIsPreviewLoading(true);
    try {
      const response = await fetchPreviewByClaims(parsed);
      setPreviewData(response);
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleStartImport = async (): Promise<boolean> => {
    const parsed = parseClaimNumbers(claimNumbers);
    if (parsed.length === 0) return false;

    setIsStarting(true);
    try {
      const response = await startImportByClaims(parsed);
      setStartResponse(response);
      onImportStarted?.();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsStarting(false);
    }
  };

  if (!open) return null;

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      <Form>
        <Stepper
          isDialog
          dialogProps={{
            isDialog: true,
            open: true,
            maxWidth: false,
            PaperProps: {
              style: {
                height: '80vh',
                width: '1000px',
              },
            },
            onClose: handleClose,
            title: 'Import by Claims',
            subheader: (
              <div className="pb-8 pr-24">
                <AlertBanner
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  withIcon
                  title="The claims loading process cannot be easily reverted."
                  note="Once claims are loaded, any changes or updates to them may require manual adjustments. Please review your selection carefully before proceeding."
                />
              </div>
            ),
          }}
          onComplete={handleClose}
          onStepChange={() => {}}
          nextBtnText="Start Import"
          backBtnText="Back"
          finishBtnText="Close"
          actionBtnText=""
          firstStepBackBtnText="Back"
          firstStepNextBtnText="Preview"
          contentClassName="flex justify-center p-24"
          headerClassName="hidden"
          stepsContainerClassName="flex-1 flex flex-col"
        >
          <Stepper.Step
            label="Enter Claims"
            onNext={handlePreview}
            onBack={() => {}}
            disableBack={true}
            disableNext={parseClaimNumbers(claimNumbers).length === 0 || isPreviewLoading}
          >
            <ClaimNumbersInputStep claimNumbers={claimNumbers} onClaimNumbersChange={setClaimNumbers} />
          </Stepper.Step>
          <Stepper.Step
            label="Selected claims preview"
            onNext={handleStartImport}
            onBack={() => {}}
            disableBack={false}
            disableNext={!previewData || previewData.total_processable_claims === 0 || isStarting}
          >
            <ClaimPreviewStep previewData={previewData} isLoading={isPreviewLoading} isStarting={isStarting} />
          </Stepper.Step>
          <Stepper.Step label="Done" onNext={() => {}} onBack={() => {}} disableBack={true} disableNext={false}>
            <ImportConfirmationStep totalClaims={startResponse?.total_claims ?? 0} />
          </Stepper.Step>
        </Stepper>
      </Form>
    </Formik>
  );
};

export default ImportByClaimsDialog;
